import * as React from "react";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { REFINED } from "../risikoanalyseStep/tokens";
import { AufzaehlungsLeiste, useAufzaehlung } from "./widgets/useAufzaehlung";
import { KarteContext, KartenKlappContext } from "./kartenKlapp";
import { SperrWert } from "./SperrWert";

/**
 * Präsentations-Bausteine der Firma/Privat-Kontaktanlage im „Refined Classic"-
 * Stil (siehe courte/designs/): ruhige weiße Karten mit Haarlinie + weichem
 * Schatten, Sektionsköpfe mit vertikalem Aubergine-Balken + Uppercase-Label
 * (+ optionalem Instrument-Serif-Heading), Inputs mit echten States (Hover,
 * Fokus-Ring, Pflichtfeld-Fehler). Native Controls, mit REFINED-Tokens
 * gestylt. Reine Präsentation — kein Datenzugriff.
 *
 * Nur die beiden Create-Forms nutzen diese Primitives, daher ist der
 * Restyle hier vollständig auf die Kontaktanlage begrenzt.
 */

// Down-Chevron für Selects (ink3), als data-URI-Hintergrund.
const SELECT_CHEVRON =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none' stroke='%23a294a0' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M4 6l4 4 4-4'/%3E%3C/svg%3E\")";

export const useFormPrimitiveStyles = makeStyles({
  card: {
    backgroundColor: REFINED.white,
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("16px"),
    ...shorthands.padding("24px"),
    boxShadow: REFINED.cardShadow,
    display: "flex",
    flexDirection: "column",
    rowGap: "16px",
  },
  // Verschachtelte Karten (Repeater wie „Weitere Anschrift") ruhiger: getönt,
  // schmaler, ohne Schatten — damit sie sich klar unterordnen.
  cardNested: {
    backgroundColor: REFINED.aub50,
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("12px"),
    ...shorthands.padding("18px"),
    boxShadow: "none",
    display: "flex",
    flexDirection: "column",
    rowGap: "14px",
  },
  head: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    columnGap: "12px",
  },
  headLeft: {
    display: "flex",
    alignItems: "stretch",
    columnGap: "12px",
    minWidth: 0,
  },
  collapseBtn: {
    background: "none",
    ...shorthands.borderWidth("0"),
    ...shorthands.padding("0"),
    marginRight: "2px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: REFINED.ink3,
    transitionProperty: "color, transform",
    transitionDuration: "140ms",
    transitionTimingFunction: "ease",
    ":hover": { color: REFINED.aub },
  },
  repeaterDivider: {
    height: "1px",
    backgroundColor: REFINED.line,
    ...shorthands.margin("16px", "0"),
    ...shorthands.borderWidth("0"),
  },
  repeaterItem: {
    display: "flex",
    flexDirection: "column",
    rowGap: "14px",
    position: "relative",
  },
  repeaterHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "4px",
  },
  repeaterTitle: {
    fontFamily: REFINED.fontUI,
    fontSize: "0.88rem",
    fontWeight: 700,
    color: REFINED.ink,
  },
  bar: {
    width: "4px",
    minHeight: "16px",
    ...shorthands.borderRadius("999px"),
    backgroundColor: REFINED.aub,
    flexShrink: 0,
  },
  headText: {
    display: "flex",
    flexDirection: "column",
    rowGap: "3px",
    minWidth: 0,
  },
  eyebrow: {
    display: "block",
    fontFamily: REFINED.fontUI,
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.085em",
    textTransform: "uppercase",
    color: REFINED.aub,
  },
  heading: {
    display: "block",
    fontFamily: REFINED.fontUI,
    fontSize: "1.15rem",
    lineHeight: "1.2",
    fontWeight: 700,
    color: REFINED.ink,
  },
  headToggle: {
    flexGrow: 1,
    alignItems: "center",
    ...shorthands.border("0"),
    backgroundColor: "transparent",
    ...shorthands.padding("0"),
    ...shorthands.margin("0"),
    textAlign: "left",
    cursor: "pointer",
    color: "inherit",
    fontFamily: "inherit",
    ":focus-visible": {
      outlineStyle: "solid",
      outlineWidth: "2px",
      outlineColor: REFINED.aub,
      outlineOffset: "4px",
      ...shorthands.borderRadius("6px"),
    },
  },
  chevron: {
    color: REFINED.ink3,
    flexShrink: 0,
    transitionProperty: "transform",
    transitionDuration: "140ms",
  },
  chevronOffen: { transform: "rotate(90deg)" },
  kopfKurz: {
    fontFamily: REFINED.fontUI,
    fontSize: "0.85rem",
    color: REFINED.ink3,
    marginLeft: "8px",
    minWidth: 0,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  kopfFehlt: {
    fontFamily: REFINED.fontUI,
    fontSize: "0.82rem",
    fontWeight: 600,
    color: REFINED.err,
    marginLeft: "8px",
    whiteSpace: "nowrap",
  },
  body: { display: "flex", flexDirection: "column", rowGap: "16px" },
  bodyZu: { display: "none" },
  row: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "18px",
    alignItems: "start",
    "@media (max-width: 600px)": { gridTemplateColumns: "1fr" },
  },
  field: { display: "grid", rowGap: "7px" },
  // Aktionsknopf neben dem Feld (Anrufen, Mail, Karte) als AnsichtsKnopf —
  // <span role="button"> braucht den Fokusring selbst.
  aktionsKnopf: {
    ":focus-visible": {
      outlineStyle: "solid",
      outlineWidth: "2px",
      outlineColor: REFINED.aub,
      outlineOffset: "2px",
    },
  },
  fieldWithSlot: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    columnGap: "8px",
    alignItems: "stretch",
  },
  label: {
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.875rem",
    color: REFINED.ink,
  },
  req: { color: REFINED.mag, fontWeight: 700 },
  inputWrap: { position: "relative", display: "flex", alignItems: "center", width: "100%" },
  inputIcon: {
    position: "absolute",
    left: "13px",
    display: "inline-flex",
    alignItems: "center",
    color: REFINED.ink3,
    pointerEvents: "none",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    ...shorthands.padding("11px", "13px"),
    fontFamily: REFINED.fontUI,
    fontWeight: 450,
    fontSize: "0.95rem",
    color: REFINED.ink,
    backgroundColor: REFINED.white,
    outlineStyle: "none",
    transitionProperty: "border-color, box-shadow",
    transitionDuration: "140ms",
    transitionTimingFunction: "ease",
    "::placeholder": { color: REFINED.ink3 },
    ":hover": { ...shorthands.borderColor(REFINED.aub) },
    ":focus": { ...shorthands.borderColor(REFINED.aub), boxShadow: REFINED.focusRing },
  },
  inputWithIcon: { paddingLeft: "38px" },
  /** Mehrzeilig: nur vertikal ziehbar, damit die Spaltenbreite erhalten bleibt. */
  textarea: { resize: "vertical", minHeight: "96px", lineHeight: 1.45 },
  selectControl: {
    appearance: "none",
    WebkitAppearance: "none",
    paddingRight: "38px",
    backgroundImage: SELECT_CHEVRON,
    backgroundRepeat: "no-repeat",
    backgroundPositionX: "right 12px",
    backgroundPositionY: "center",
    cursor: "pointer",
  },
  inputInvalid: {
    ...shorthands.borderColor(REFINED.err),
    backgroundColor: REFINED.errBg,
    ":hover": { ...shorthands.borderColor(REFINED.err) },
    ":focus": { ...shorthands.borderColor(REFINED.err), boxShadow: REFINED.errRing },
  },
  errorMsg: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    fontFamily: REFINED.fontUI,
    fontSize: "0.8rem",
    fontWeight: 500,
    color: REFINED.err,
  },
  hintMsg: {
    fontFamily: REFINED.fontUI,
    fontSize: "0.8rem",
    fontWeight: 450,
    color: REFINED.ink3,
    lineHeight: 1.35,
  },
  checkRow: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
    cursor: "pointer",
    fontFamily: REFINED.fontUI,
    fontSize: "0.9rem",
    color: REFINED.ink,
    "& input": { accentColor: REFINED.aub, cursor: "pointer" },
  },
  radioRow: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
    cursor: "pointer",
    fontFamily: REFINED.fontUI,
    fontSize: "0.9rem",
    color: REFINED.ink,
    "& input": { accentColor: REFINED.aub, cursor: "pointer" },
  },
});

// ── Leading-Icons für Kontaktfelder (16px, erben Farbe via currentColor) ────
const ICON: React.SVGProps<SVGSVGElement> = {
  width: 16,
  height: 16,
  viewBox: "0 0 16 16",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};
export const IconPhone: React.FC = () => (
  <svg {...ICON}>
    <path d="M3 4c0 5 4 9 9 9 .7 0 1.2-.5 1.2-1.2v-1.5c0-.5-.3-.9-.8-1l-1.9-.4c-.4-.1-.8 0-1 .4l-.5.7c-1.4-.7-2.5-1.8-3.2-3.2l.7-.5c.3-.3.5-.6.4-1l-.4-1.9c-.1-.5-.5-.8-1-.8H4.2C3.5 2.8 3 3.3 3 4Z" />
  </svg>
);
export const IconMail: React.FC = () => (
  <svg {...ICON}>
    <rect x="2.2" y="3.6" width="11.6" height="8.8" rx="1.6" />
    <path d="M2.7 4.6 8 8.4l5.3-3.8" />
  </svg>
);
export const IconGlobe: React.FC = () => (
  <svg {...ICON}>
    <circle cx="8" cy="8" r="6" />
    <path d="M2 8h12" />
    <path d="M8 2c1.7 1.7 2.6 3.8 2.6 6S9.7 12.3 8 14c-1.7-1.7-2.6-3.8-2.6-6S6.3 3.7 8 2Z" />
  </svg>
);
/** Visitenkarte: Karte mit Porträt links und zwei Textzeilen rechts. */
export const IconVisitenkarte: React.FC = () => (
  <svg {...ICON}>
    <rect x="1.8" y="3.4" width="12.4" height="9.2" rx="1.5" />
    <circle cx="5.3" cy="7.1" r="1.4" />
    <path d="M3.3 11.1c.3-1 1.1-1.6 2-1.6s1.7.6 2 1.6" />
    <path d="M9.4 6.6h3.1M9.4 9h3.1" />
  </svg>
);

const ErrorIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
    <circle cx="8" cy="8" r="6.4" />
    <path d="M8 4.8v3.6" strokeLinecap="round" />
    <circle cx="8" cy="11.1" r="0.5" fill="currentColor" stroke="none" />
  </svg>
);

export const SectionCard: React.FC<{
  /** Uppercase-Eyebrow über dem Inhalt (mit vertikalem Aubergine-Balken). */
  title?: string;
  /** Optionales großes Heading in Instrument Serif (z. B. „Firmenangaben"). */
  heading?: string;
  /** Getönte, schmalere Variante für verschachtelte Repeater-Karten (legacy). */
  nested?: boolean;
  /** Ermöglicht das Ein-/Ausklappen der Sektion. */
  collapsible?: boolean;
  /** Standardmäßig eingeklappt? */
  defaultCollapsed?: boolean;
  actions?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}> = ({
  title,
  heading,
  nested,
  collapsible = false,
  defaultCollapsed = false,
  actions,
  className,
  children,
}) => {
  const s = useFormPrimitiveStyles();
  const klapp = React.useContext(KartenKlappContext);
  const aussen = React.useContext(KarteContext);
  const eigeneId = title ?? heading ?? null;
  // Klappbar sind Hauptkarten unter einem Klapp-Rahmen (siehe kartenKlapp.tsx);
  // ohne Rahmen nur, wenn die Karte selbst `collapsible` ist.
  const imRahmen = !!klapp && !!eigeneId && !nested && !aussen.innerhalb;
  const klappbar = imRahmen || (collapsible && !!eigeneId);
  const [lokalZu, setLokalZu] = React.useState(defaultCollapsed);
  const anmelden = klapp?.anmelden;
  React.useEffect(() => {
    if (!imRahmen || !anmelden || !eigeneId) return;
    return anmelden(eigeneId);
  }, [imRahmen, anmelden, eigeneId]);
  // `defaultCollapsed` gilt auch im Rahmen — einmalig beim ersten Anzeigen.
  const startZuGesetzt = React.useRef(false);
  React.useEffect(() => {
    if (!imRahmen || startZuGesetzt.current) return;
    startZuGesetzt.current = true;
    if (defaultCollapsed && klapp!.istOffen(eigeneId!)) klapp!.umschalten(eigeneId!);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imRahmen]);
  const bodyId = React.useId();

  const offen = !klappbar || (imRahmen ? klapp!.istOffen(eigeneId!) : !lokalZu);
  const umschaltenKlick = () => (imRahmen ? klapp!.umschalten(eigeneId!) : setLokalZu((z) => !z));
  const fehlt = imRahmen && !offen && klapp!.fehlt(eigeneId!);
  const kurz = imRahmen && !offen && !fehlt ? klapp!.kurzfassung(eigeneId!) : null;
  // Im Klapp-Kopf steckt alles in einem Knopf — dort sind nur Spans erlaubt.
  const T = klappbar ? "span" : "div";
  const kopfText = (
    <>
      {(title || heading) && <span className={s.bar} />}
      <T className={s.headText}>
        {title && <T className={s.eyebrow}>{title}</T>}
        {heading && <T className={s.heading}>{heading}</T>}
      </T>
    </>
  );

  return (
    <KarteContext.Provider value={{ id: aussen.id ?? eigeneId, innerhalb: true }}>
      <section
        className={mergeClasses(nested ? s.cardNested : s.card, className)}
        data-karte={imRahmen ? eigeneId! : undefined}
      >
        {(title || heading || actions) && (
          <div className={s.head}>
            {klappbar ? (
              // Kein <button>: Auf- und Zuklappen ändert nichts und muss auch in
              // der gesperrten Akte (`<fieldset disabled>`) gehen — wie AnsichtsKnopf.
              <span
                role="button"
                tabIndex={0}
                className={mergeClasses(s.headLeft, s.headToggle)}
                aria-expanded={offen}
                aria-controls={bodyId}
                onClick={umschaltenKlick}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    umschaltenKlick();
                  }
                }}
              >
                <svg
                  className={mergeClasses(s.chevron, offen && s.chevronOffen)}
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M6 4l4 4-4 4" />
                </svg>
                {kopfText}
                {fehlt ? (
                  <span className={s.kopfFehlt}>Pflichtfeld fehlt</span>
                ) : kurz ? (
                  <span className={s.kopfKurz}>{kurz}</span>
                ) : null}
              </span>
            ) : (
              <div className={s.headLeft}>{kopfText}</div>
            )}
            {actions}
          </div>
        )}
        {klappbar ? (
          <div id={bodyId} hidden={!offen} className={mergeClasses(s.body, !offen && s.bodyZu)}>
            {children}
          </div>
        ) : (
          children
        )}
      </section>
    </KarteContext.Provider>
  );
};

export const FieldRow: React.FC<{
  columns?: 1 | 2;
  children: React.ReactNode;
}> = ({ columns = 2, children }) => {
  const s = useFormPrimitiveStyles();
  return <div className={columns === 2 ? s.row : s.field}>{children}</div>;
};

export const LabeledInput: React.FC<{
  label: string;
  name: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
  /** `time` seit 09/2026: Die Geburtszeit steht auf der Urkunde. */
  type?: "text" | "date" | "time" | "email" | "tel" | "number";
  invalid?: boolean;
  rightSlot?: React.ReactNode;
  /** Führendes Icon (z. B. Telefon/E-Mail/Web). */
  leftIcon?: React.ReactNode;
  /** Inline-Fehlertext; erscheint, sobald das Feld berührt + invalid ist. */
  errorText?: string;
  /**
   * Erklärender Hinweis unter dem Feld — für Felder, deren Zweck sich aus dem
   * Label allein nicht erschließt. Weicht dem Fehlertext, damit unter dem Feld
   * nie zwei Meldungen übereinander stehen.
   */
  hint?: string;
  /**
   * Zeichenbegrenzung — für Felder, deren Spalte kürzer ist als „beliebig"
   * (z. B. Bemerkung: varchar(255)). Ohne sie liefe die Eingabe erst beim
   * Speichern in einen Serverfehler.
   */
  maxLength?: number;
}> = ({ label, name, value, onChange, placeholder, required, type = "text", invalid, rightSlot, leftIcon, errorText, hint, maxLength }) => {
  const s = useFormPrimitiveStyles();
  const [touched, setTouched] = React.useState(false);
  const showError = !!invalid && touched;
  const inputEl = (
    <div className={s.inputWrap}>
      {leftIcon && <span className={s.inputIcon}>{leftIcon}</span>}
      <input
        id={name}
        name={name}
        type={type}
        // CRM-Felder enthalten Kundendaten – Browser-Autofill (eigene Adresse/
        // Bankverbindung des Mitarbeiters vorschlagen bzw. Kundendaten ins
        // Browser-Profil speichern) hier unerwünscht.
        autoComplete="off"
        className={mergeClasses(s.input, !!leftIcon && s.inputWithIcon, showError && s.inputInvalid)}
        value={value}
        placeholder={placeholder}
        maxLength={maxLength}
        aria-invalid={showError || undefined}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setTouched(true)}
      />
      <SperrWert />
    </div>
  );
  return (
    <div className={s.field}>
      <label className={s.label} htmlFor={name}>
        {label}
        {required && <span className={s.req}> *</span>}
      </label>
      {rightSlot ? (
        <div className={s.fieldWithSlot}>
          {inputEl}
          {rightSlot}
        </div>
      ) : (
        inputEl
      )}
      {showError && errorText ? (
        <div className={s.errorMsg}>
          <ErrorIcon />
          {errorText}
        </div>
      ) : (
        hint && <div className={s.hintMsg}>{hint}</div>
      )}
    </div>
  );
};

/**
 * Mehrzeiliges Textfeld — für Angaben, die in eine Zeile nicht passen
 * (Bemerkung, Tätigkeitsbeschreibung). Gleiche Optik wie {@link LabeledInput},
 * nur höher und in der Höhe ziehbar.
 */
export const LabeledTextarea: React.FC<{
  label: string;
  name: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  /** Sichtbare Zeilen im Ausgangszustand. */
  rows?: number;
  hint?: string;
  maxLength?: number;
  /**
   * Gliederung anbieten: Knopfleiste über dem Feld, Enter setzt den nächsten
   * Aufzählungspunkt fort, Tab rückt eine Ebene ein. Gespeichert wird weiterhin
   * reiner Text ({@link ../../utils/aufzaehlungText}).
   */
  aufzaehlung?: boolean;
}> = ({ label, name, value, onChange, placeholder, rows = 4, hint, maxLength, aufzaehlung }) => {
  const s = useFormPrimitiveStyles();
  // Der Hook läuft immer (Regeln der Hooks); ohne `aufzaehlung` bleibt er ungenutzt.
  const auf = useAufzaehlung(value, onChange);
  return (
    <div className={s.field}>
      <label className={s.label} htmlFor={name}>{label}</label>
      {aufzaehlung && <AufzaehlungsLeiste aktionen={auf.aktionen} />}
      <textarea
        id={name}
        name={name}
        ref={aufzaehlung ? auf.ref : undefined}
        className={mergeClasses(s.input, s.textarea)}
        value={value}
        rows={rows}
        placeholder={placeholder}
        maxLength={maxLength}
        autoComplete="off"
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={aufzaehlung ? auf.onKeyDown : undefined}
      />
      <SperrWert mehrzeilig />
      {hint && <div className={s.hintMsg}>{hint}</div>}
    </div>
  );
};

export const LabeledSelect: React.FC<{
  label: string;
  name: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
  required?: boolean;
  invalid?: boolean;
}> = ({ label, name, value, onChange, options, placeholder = "Bitte wählen", required, invalid }) => {
  const s = useFormPrimitiveStyles();
  const [touched, setTouched] = React.useState(false);
  const showError = !!invalid && touched;
  return (
    <div className={s.field}>
      <label className={s.label} htmlFor={name}>
        {label}
        {required && <span className={s.req}> *</span>}
      </label>
      <select
        id={name}
        name={name}
        className={mergeClasses(s.input, s.selectControl, showError && s.inputInvalid)}
        value={value}
        aria-invalid={showError || undefined}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setTouched(true)}
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <SperrWert />
    </div>
  );
};

export const LabeledCheckbox: React.FC<{
  label: string;
  checked: boolean;
  onChange: (b: boolean) => void;
  disabled?: boolean;
}> = ({ label, checked, onChange, disabled }) => {
  const s = useFormPrimitiveStyles();
  return (
    <label className={s.checkRow}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
    </label>
  );
};

export const LabeledRadio: React.FC<{
  label: string;
  name: string;
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}> = ({ label, name, checked, onChange, disabled }) => {
  const s = useFormPrimitiveStyles();
  return (
    <label className={s.radioRow}>
      <input
        type="radio"
        name={name}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
      />
      {label}
    </label>
  );
};
